import {
  ApolloClient,
  ApolloLink,
  type FetchResult,
  HttpLink,
  InMemoryCache,
  Observable,
  type Operation,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { captureException } from "@sentry/browser";

export const API_DEFAULT_PROVIDER = "default";
export const API_CMS_PROVIDER = "cms";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createRetryLink({
  maxRetries = MAX_RETRIES,
  retryDelay = RETRY_DELAY,
}: RetryOptions = {}) {
  return new ApolloLink((operation, forward) => {
    let retryCount = 0;

    return new Observable((observer) => {
      let subscription: { unsubscribe: () => void } | undefined;

      const executeRequest = () => {
        if (!forward) {
          return;
        }

        subscription = forward(operation).subscribe({
          complete: () => {
            observer.complete();
          },
          error: async (error: Error) => {
            const isNetworkError =
              error.message.includes("fetch failed") ||
              error.message.includes("NetworkError") ||
              error.message.includes("Failed to fetch") ||
              error.message.includes("Network request failed");

            if (isNetworkError && retryCount < maxRetries) {
              retryCount++;
              console.warn(
                `Apollo fetch failed, retry attempt ${retryCount}/${maxRetries}:`,
                error.message
              );

              await sleep(retryDelay * retryCount);
              executeRequest();
            } else {
              if (retryCount > 0) {
                captureException(error, {
                  extra: {
                    finalError: error.message,
                    operationName: operation.operationName,
                    retryAttempts: retryCount,
                    variables: operation.variables,
                  },
                  tags: {
                    apollo_operation: operation.operationName,
                    apollo_retry_count: retryCount,
                  },
                });
              } else {
                captureException(error, {
                  extra: {
                    operationName: operation.operationName,
                    variables: operation.variables,
                  },
                  tags: {
                    apollo_operation: operation.operationName,
                  },
                });
              }

              observer.error(error);
            }
          },
          next: (result: FetchResult) => {
            observer.next(result);
          },
        });
      };

      executeRequest();

      return () => {
        subscription?.unsubscribe();
      };
    });
  });
}

function createErrorLoggingLink() {
  return onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      for (const error of graphQLErrors) {
        captureException(new Error(`GraphQL Error: ${error.message}`), {
          extra: {
            extensions: error.extensions,
            operationName: operation.operationName,
            path: error.path,
          },
          tags: {
            apollo_error_type: "graphqL_error",
            apollo_operation: operation.operationName,
          },
        });
      }
    }

    if (networkError) {
      captureException(networkError, {
        extra: {
          message: networkError.message,
          operationName: operation.operationName,
        },
        tags: {
          apollo_error_type: "network_error",
          apollo_operation: operation.operationName,
        },
      });
    }
  });
}

export const cache = new InMemoryCache({
  typePolicies: {
    BadgeType: {
      keyFields: ["id", "earnedAt"],
    },
    GameType: {
      fields: {
        cacheKey: {
          read: (cacheKey, { readField }) =>
            cacheKey || readField("id") || null,
        },
      },
      keyFields: ["cacheKey"],
    },
    LocationType: {
      fields: {
        cacheKey: {
          read: (cacheKey, { readField }) =>
            cacheKey || readField("id") || null,
        },
      },
      keyFields: ["cacheKey"],
    },
    Query: {
      fields: {
        games: {
          keyArgs: [
            "boundingBox",
            "distance",
            "escapedOnly",
            "excludeNotPlayed",
            "excludePlayed",
            "excludeSaved",
            "filters",
            "groupByLocation",
            "includeAllGames",
            "isUser",
            "lat",
            "lng",
            "pageSize",
            "query",
            "savedOnly",
            "sortBy",
            "userId",
            "userIds",
            "username",
          ],
          merge: (existing, incoming) => {
            if (!existing) {
              return incoming;
            }
            // biome-ignore lint/suspicious/noExplicitAny: legacy
            const oldObjects = existing.objects.map(({ __ref }: any) => __ref);
            const objects = incoming.objects.reduce(
              // biome-ignore lint/suspicious/noExplicitAny: legacy
              (accum: any[], obj: any) => {
                if (!oldObjects.includes(obj.__ref)) {
                  accum.push(obj);
                }
                return accum;
              },
              [...existing.objects]
            );

            return { ...incoming, objects };
          },
        },
      },
    },
  },
});

export default function apollo() {
  if (!process.env.API_URL) {
    throw new Error(
      "API_URL is not set. Check .env and vite.config.ts define block."
    );
  }

  const api = new HttpLink({ uri: process.env.API_URL });
  const cms = new HttpLink({
    headers: { authorization: `Bearer ${process.env.CMS_API_TOKEN}` },
    uri: process.env.CMS_API_URL || "",
  });

  const isCmsRequest = (op: Operation) =>
    op.getContext().clientName === API_CMS_PROVIDER;

  const errorLoggingLink = createErrorLoggingLink();
  const retryLink = createRetryLink();

  return new ApolloClient({
    cache,
    link: ApolloLink.from([
      errorLoggingLink,
      retryLink,
      ApolloLink.split(isCmsRequest, cms, api),
    ]),
    ssrMode: typeof window === "undefined",
  });
}
